import { attackOutcome, damageOf } from './attack.js';
export const INFERRED_LIMITS = { defences: 12, hits: 500, number: 100_000, label: 80 };
/**
 * The evidence an attack gives the players, or null when it gives none: a private roll (no player sees its total), a
 * target no player could see, a defence the target's conditions changed for this attack (the number compared was not
 * the creature's own), an attack still waiting for its defence, a result a natural roll decided (3.5's 20 and 1), or a
 * miss the miss chance made. Damage counts on a player's own (public) hit alone: the DM's damage dice reach no player.
 */
export function evidenceOf(roll) {
    const f = roll.attack;
    // An attack against the system's fixed number (issue 1312) tells nothing of the target's defences.
    // An outcome the DM changed (issue 1499; workbench §5.2) is not the number's verdict: it says nothing of the defence.
    if (!f || roll.private || !f.target.seen || f.ac === null || f.acTerms?.length || f.rules.against === 'number' || f.changed)
        return null;
    const outcome = attackOutcome(roll, f);
    if (outcome.result === 'unresolved' || outcome.concealed?.missed)
        return null;
    if (f.rules.natural.some((n) => n.face === outcome.natural))
        return null;
    const hit = outcome.result === 'hit' || outcome.result === 'critical';
    const playerRoll = f.attacker.owners.includes(roll.actor.id);
    const dmg = hit && playerRoll ? damageOf(f, outcome) : null;
    return { defence: f.defence, label: f.rules.defence, total: outcome.total, hit, damage: dmg ? dmg.total : null };
}
/**
 * Fold one attack's evidence in (pure). A hit at T says the defence is at most T; a miss at T, at least T + 1. When the
 * new bound contradicts the other (a fudge, a defence that changed), the newest wins and the older bound is dropped.
 */
export function foldEvidence(inf, e) {
    const was = inf?.defences[e.defence] ?? { label: e.label, low: null, high: null, from: 0 };
    let { low, high } = was;
    if (e.hit) {
        high = high === null ? e.total : Math.min(high, e.total);
        if (low !== null && low > high)
            low = null;
    }
    else {
        low = low === null ? e.total + 1 : Math.max(low, e.total + 1);
        if (high !== null && low > high)
            high = null;
    }
    const defences = { ...(inf?.defences ?? {}), [e.defence]: { label: e.label, low, high, from: was.from + 1 } };
    const hits = inf ? [...inf.damage.hits] : [];
    if (e.damage !== null)
        hits.push(e.damage);
    const kept = hits.slice(-INFERRED_LIMITS.hits);
    return { defences, damage: { dealt: kept.reduce((s, n) => s + n, 0), hits: kept } };
}
/** Fold a run of attacks, oldest first: undefined when none of them gave evidence. */
export function inferAttacks(rolls) {
    let inf;
    for (const r of rolls) {
        const e = evidenceOf(r);
        if (e)
            inf = foldEvidence(inf, e);
    }
    return inf;
}
const isNum = (v) => typeof v === 'number' && Number.isInteger(v) && Math.abs(v) <= INFERRED_LIMITS.number;
const isLabel = (v) => typeof v === 'string' && v.trim().length > 0 && v.length <= INFERRED_LIMITS.label && !v.includes('\u0000');
const KEY_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
/** One attack's evidence from anywhere (a stored body): exact shape or null. */
export function parseEvidence(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const e = v;
    if (typeof e.defence !== 'string' || !KEY_RE.test(e.defence) || !isLabel(e.label) || !isNum(e.total) || typeof e.hit !== 'boolean')
        return null;
    if (!(e.damage === null || (isNum(e.damage) && e.damage >= 0)))
        return null;
    return { defence: e.defence, label: e.label, total: e.total, hit: e.hit, damage: e.damage };
}
/** What was worked out, from a stored object: exact shape or null. */
export function parseInferred(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const { defences, damage } = v;
    if (typeof defences !== 'object' || defences === null || Array.isArray(defences))
        return null;
    const entries = Object.entries(defences);
    if (entries.length > INFERRED_LIMITS.defences)
        return null;
    const out = { defences: {}, damage: { dealt: 0, hits: [] } };
    for (const [key, d] of entries) {
        if (!KEY_RE.test(key) || typeof d !== 'object' || d === null)
            return null;
        const r = d;
        if (!isLabel(r.label) || !(r.low === null || isNum(r.low)) || !(r.high === null || isNum(r.high)) || !isNum(r.from) || r.from < 1)
            return null;
        out.defences[key] = { label: r.label, low: r.low, high: r.high, from: r.from };
    }
    const dmg = damage;
    if (typeof dmg !== 'object' || dmg === null || !Array.isArray(dmg.hits) || dmg.hits.length > INFERRED_LIMITS.hits || !dmg.hits.every((h) => isNum(h) && h >= 0))
        return null;
    out.damage = { dealt: dmg.hits.reduce((s, n) => s + n, 0), hits: [...dmg.hits] };
    return out;
}
const DR_LABEL = /^\s*(dr|damage reduction)\s*$/i;
/** The damage reduction a revealed fact names ("DR" or "Damage Reduction", case aside, whose value starts with a number: "5/magic"), or null. */
export function reductionOf(facts) {
    const f = facts?.find((x) => DR_LABEL.test(x.label));
    const m = f ? /^\s*(\d+)/.exec(f.value) : null;
    return m ? Number(m[1]) : null;
}
/** About what the hits dealt after a reduction: each hit less it, never below nothing. */
export const afterReduction = (damage, dr) => damage.hits.reduce((s, h) => s + Math.max(0, h - dr), 0);
/** What a player is sent (issue 1133): the ranges and the damage as a sum, with the estimate after a revealed reduction. */
export function forPlayers(inf, facts) {
    const dr = reductionOf(facts);
    return {
        defences: Object.fromEntries(Object.entries(inf.defences).map(([k, d]) => [k, { ...d }])),
        damage: {
            dealt: inf.damage.dealt, hits: [], count: inf.damage.hits.length,
            ...(dr !== null && inf.damage.hits.length ? { afterDr: { dr, amount: afterReduction(inf.damage, dr) } } : {}),
        },
    };
}
//# sourceMappingURL=inferred.js.map